import React, { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import { useAuth } from "../auth/AuthProvider";
import { db } from "../lib/firebase";
import SchoolSelector from "./SchoolSelector";
import { collection, doc, getDoc, onSnapshot } from "firebase/firestore";

// ===== Google Maps JS API loader (loads once, shared) =====
let mapsLoaderPromise = null;
function loadGoogleMaps(apiKey) {
  if (window.google?.maps?.Map) return Promise.resolve();
  if (mapsLoaderPromise) return mapsLoaderPromise;
  mapsLoaderPromise = new Promise((resolve, reject) => {
    const callbackName = "__ridewatchMapsInit";
    window[callbackName] = () => {
      resolve();
      delete window[callbackName];
    };
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      apiKey
    )}&callback=${callbackName}&loading=async`;
    script.async = true;
    script.onerror = () => {
      mapsLoaderPromise = null;
      reject(new Error("Failed to load Google Maps. Check the API key and network."));
    };
    document.head.appendChild(script);
  });
  return mapsLoaderPromise;
}

// ===== Helpers =====
const ROUTE_COLORS = [
  "#4f46e5", "#059669", "#d97706", "#dc2626",
  "#0891b2", "#7c3aed", "#db2777", "#65a30d",
];
function colorForRoute(routeId) {
  let hash = 0;
  for (let i = 0; i < routeId.length; i++) hash = (hash * 31 + routeId.charCodeAt(i)) >>> 0;
  return ROUTE_COLORS[hash % ROUTE_COLORS.length];
}

const STALE_AFTER_MS = 2 * 60 * 1000; // no update for 2 min = stale
const HIDE_AFTER_MS = 15 * 60 * 1000; // no update for 15 min = hidden from the map entirely

function toMillis(ts) {
  if (!ts) return null;
  if (typeof ts.toMillis === "function") return ts.toMillis();
  if (ts.seconds != null) return ts.seconds * 1000;
  return null;
}

function formatAgo(ms, now) {
  if (ms == null) return "just now";
  const diff = Math.max(0, now - ms);
  if (diff < 15000) return "just now";
  if (diff < 60000) return `${Math.round(diff / 1000)}s ago`;
  if (diff < 3600000) return `${Math.round(diff / 60000)}m ago`;
  return `${Math.round(diff / 3600000)}h ago`;
}

const LiveBusMapLayer = () => {
  const { user, profile, loading } = useAuth();
  const schoolId = profile?.current_school_id || null;
  const canUse = useMemo(() => !!user && !!schoolId, [user, schoolId]);

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

  const [err, setErr] = useState("");
  const [mapsReady, setMapsReady] = useState(false);
  const [mapsError, setMapsError] = useState("");

  // Firestore data
  const [routes, setRoutes] = useState(null); // null = loading
  const [busesByRoute, setBusesByRoute] = useState({}); // routeId -> [{driverId, latitude, longitude, timestamp, ...}]

  // Driver profiles cache (uid -> {displayName, email} | null while pending)
  const driverInfoRef = useRef({});
  const [, setDriverTick] = useState(0);

  // Re-render every 30s so "last update" freshness stays accurate
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  // Map objects
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef(new Map()); // key -> google.maps.Marker
  const infoWindowRef = useRef(null);
  const busesRef = useRef([]);

  // ===== Load Google Maps script =====
  useEffect(() => {
    if (!apiKey) return;
    let cancelled = false;
    loadGoogleMaps(apiKey)
      .then(() => !cancelled && setMapsReady(true))
      .catch((e) => !cancelled && setMapsError(e.message));
    return () => {
      cancelled = true;
    };
  }, [apiKey]);

  // ===== Subscribe to the school's routes =====
  useEffect(() => {
    if (!canUse) {
      setRoutes([]);
      return;
    }
    setRoutes(null);
    setErr("");
    const unsub = onSnapshot(
      collection(db, "schools", schoolId, "routes"),
      (snap) => setRoutes(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (e) => {
        setErr(e?.message || "Failed to load routes.");
        setRoutes([]);
      }
    );
    return () => unsub();
  }, [canUse, schoolId]);

  const routesById = useMemo(() => {
    const m = {};
    (routes || []).forEach((r) => (m[r.id] = r));
    return m;
  }, [routes]);

  // ===== Subscribe to active_drivers for every route =====
  const routeIdsSig = useMemo(() => (routes || []).map((r) => r.id).sort().join(","), [routes]);
  useEffect(() => {
    setBusesByRoute({});
    if (!canUse || !routeIdsSig) return;
    const ids = routeIdsSig.split(",");
    const unsubs = ids.map((routeId) =>
      onSnapshot(
        collection(db, "schools", schoolId, "routes", routeId, "active_drivers"),
        (snap) => {
          setBusesByRoute((prev) => ({
            ...prev,
            [routeId]: snap.docs.map((d) => ({ driverId: d.id, ...d.data() })),
          }));
        },
        () => {
          // Route may have been deleted mid-subscription; ignore.
        }
      )
    );
    return () => unsubs.forEach((u) => u());
  }, [canUse, schoolId, routeIdsSig]);

  // Flat list of live buses (buses silent for over HIDE_AFTER_MS are treated as gone —
  // the driver app only cleans up its doc on a manual Stop, so force-closed apps
  // leave "ghost" docs behind until the server janitor removes them)
  const buses = useMemo(() => {
    const list = [];
    for (const [routeId, arr] of Object.entries(busesByRoute)) {
      for (const b of arr) {
        if (typeof b.latitude !== "number" || typeof b.longitude !== "number") continue;
        const ms = toMillis(b.timestamp);
        if (ms != null && now - ms > HIDE_AFTER_MS) continue;
        list.push({ key: `${routeId}_${b.driverId}`, routeId, ...b });
      }
    }
    return list;
  }, [busesByRoute, now]);
  busesRef.current = buses;

  // ===== Fetch driver names for markers/list =====
  useEffect(() => {
    const missing = [...new Set(buses.map((b) => b.driverId))].filter(
      (id) => !(id in driverInfoRef.current)
    );
    if (!missing.length) return;
    missing.forEach((id) => (driverInfoRef.current[id] = null)); // mark pending
    Promise.all(
      missing.map(async (id) => {
        try {
          const snap = await getDoc(doc(db, "users", id));
          driverInfoRef.current[id] = snap.exists() ? snap.data() : {};
        } catch {
          driverInfoRef.current[id] = {};
        }
      })
    ).then(() => setDriverTick((t) => t + 1));
  }, [buses]);

  function driverLabel(driverId) {
    const info = driverInfoRef.current[driverId];
    return info?.displayName || info?.email || "Driver";
  }

  // ===== Init map =====
  useEffect(() => {
    if (!mapsReady || !mapDivRef.current || mapRef.current) return;
    mapRef.current = new window.google.maps.Map(mapDivRef.current, {
      center: { lat: 0, lng: 0 },
      zoom: 2,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      clickableIcons: false,
    });
    infoWindowRef.current = new window.google.maps.InfoWindow();
  }, [mapsReady, canUse]);

  function openInfo(key) {
    const map = mapRef.current;
    const marker = markersRef.current.get(key);
    const bus = busesRef.current.find((b) => b.key === key);
    if (!map || !marker || !bus) return;
    const route = routesById[bus.routeId];
    const ms = toMillis(bus.timestamp);
    const div = document.createElement("div");
    div.style.minWidth = "180px";
    const title = document.createElement("strong");
    title.textContent = route?.name || "Route";
    const drv = document.createElement("div");
    drv.textContent = driverLabel(bus.driverId);
    const upd = document.createElement("div");
    upd.style.color = "#6b7280";
    upd.style.fontSize = "12px";
    upd.textContent = `Updated ${formatAgo(ms, Date.now())}`;
    div.append(title, drv, upd);
    infoWindowRef.current.setContent(div);
    infoWindowRef.current.open({ map, anchor: marker });
  }

  // ===== Sync markers with bus positions =====
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapsReady) return;
    const markers = markersRef.current;
    const liveKeys = new Set();

    buses.forEach((b) => {
      liveKeys.add(b.key);
      const pos = { lat: b.latitude, lng: b.longitude };
      const stale = (() => {
        const ms = toMillis(b.timestamp);
        return ms != null && Date.now() - ms > STALE_AFTER_MS;
      })();
      const icon = {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 16,
        fillColor: stale ? "#9ca3af" : colorForRoute(b.routeId),
        fillOpacity: 1,
        strokeColor: "#ffffff",
        strokeWeight: 2,
      };
      let m = markers.get(b.key);
      if (!m) {
        m = new window.google.maps.Marker({
          map,
          position: pos,
          icon,
          label: { text: "🚌", fontSize: "16px" },
          title: routesById[b.routeId]?.name || "Bus",
        });
        m.addListener("click", () => openInfo(b.key));
        markers.set(b.key, m);
      } else {
        m.setPosition(pos);
        m.setIcon(icon);
      }
    });

    // Remove markers for buses that stopped broadcasting
    for (const [key, m] of markers) {
      if (!liveKeys.has(key)) {
        m.setMap(null);
        markers.delete(key);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buses, mapsReady, routesById, now]);

  // ===== Fit bounds when the set of buses changes (not on every GPS tick) =====
  const busKeysSig = useMemo(() => buses.map((b) => b.key).sort().join("|"), [buses]);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapsReady || !busesRef.current.length) return;
    const bounds = new window.google.maps.LatLngBounds();
    busesRef.current.forEach((b) => bounds.extend({ lat: b.latitude, lng: b.longitude }));
    map.fitBounds(bounds, 60);
    window.google.maps.event.addListenerOnce(map, "bounds_changed", () => {
      if (map.getZoom() > 16) map.setZoom(16);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busKeysSig, mapsReady]);

  function locateBus(key) {
    const map = mapRef.current;
    const bus = busesRef.current.find((b) => b.key === key);
    if (!map || !bus) return;
    map.panTo({ lat: bus.latitude, lng: bus.longitude });
    if (map.getZoom() < 15) map.setZoom(15);
    openInfo(key);
  }

  // Cleanup markers on unmount
  useEffect(() => {
    const markers = markersRef.current;
    return () => {
      for (const [, m] of markers) m.setMap(null);
      markers.clear();
    };
  }, []);

  if (loading) return null;

  return (
    <section className="container-fluid py-4">
      {canUse && (
        <div className="mb-4">
          <SchoolSelector />
        </div>
      )}

      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h3 className="mb-1">Live Bus Map</h3>
          <p className="text-secondary mb-0">
            Real-time positions of all active buses for the selected school.
          </p>
        </div>
        <span className="badge bg-success-subtle text-success d-flex align-items-center gap-2 px-3 py-2">
          <Icon icon="mdi:bus-multiple" />
          {buses.length} active {buses.length === 1 ? "bus" : "buses"}
        </span>
      </div>

      {!canUse && (
        <div className="alert alert-warning mb-3 d-flex align-items-start gap-3">
          <Icon icon="mdi:alert-circle-outline" style={{ fontSize: "24px", marginTop: "2px" }} />
          <div>
            <h6 className="mb-2">No School Selected</h6>
            <p className="mb-2">
              You don't have an associated school yet. Please create a school first to see its
              buses.
            </p>
            <a href="/schools" className="btn btn-sm btn-warning">
              <Icon icon="mdi:plus-circle" className="me-1" />
              Create School
            </a>
          </div>
        </div>
      )}

      {!apiKey && (
        <div className="alert alert-danger mb-3">
          <h6 className="mb-1">Google Maps API key missing</h6>
          <p className="mb-0">
            Add <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> to the <code>.env</code> file (a{" "}
            <em>Maps JavaScript API</em> key) and rebuild the app.
          </p>
        </div>
      )}
      {mapsError && <div className="alert alert-danger">{mapsError}</div>}
      {err && <div className="alert alert-danger">{err}</div>}

      {canUse && (
        <div className="row g-3">
          {/* Map */}
          <div className="col-12 col-xl-8">
            <div className="card radius-12 overflow-hidden">
              <div
                ref={mapDivRef}
                style={{ width: "100%", height: "70vh", minHeight: "420px" }}
              >
                {(!apiKey || !mapsReady) && !mapsError && apiKey && (
                  <div className="d-flex align-items-center justify-content-center h-100">
                    <div className="spinner-border" role="status" />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Active buses list */}
          <div className="col-12 col-xl-4">
            <div className="card radius-12 h-100">
              <div className="card-body">
                <h6 className="mb-3 d-flex align-items-center gap-2">
                  <Icon icon="mdi:bus-clock" />
                  Active Buses
                </h6>

                {routes === null ? (
                  <div className="text-center py-4">
                    <div className="spinner-border spinner-border-sm" role="status" />
                  </div>
                ) : buses.length === 0 ? (
                  <div className="text-center py-4">
                    <Icon icon="mdi:bus-alert" style={{ fontSize: "48px", color: "#9ca3af" }} />
                    <p className="text-secondary mt-2 mb-0">
                      No buses are broadcasting right now. Buses appear here as soon as a driver
                      starts a route, and are removed after 15 minutes without a GPS update.
                    </p>
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {buses.map((b) => {
                      const ms = toMillis(b.timestamp);
                      const stale = ms != null && now - ms > STALE_AFTER_MS;
                      return (
                        <button
                          key={b.key}
                          type="button"
                          className="btn text-start border radius-12 p-3 w-100"
                          onClick={() => locateBus(b.key)}
                        >
                          <div className="d-flex align-items-center justify-content-between gap-2">
                            <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                              <span
                                className="d-inline-block rounded-circle flex-shrink-0"
                                style={{
                                  width: "12px",
                                  height: "12px",
                                  background: stale ? "#9ca3af" : colorForRoute(b.routeId),
                                }}
                              />
                              <div style={{ minWidth: 0 }}>
                                <div className="fw-semibold text-truncate">
                                  {routesById[b.routeId]?.name || "Route"}
                                </div>
                                <div className="text-secondary text-sm text-truncate">
                                  {driverLabel(b.driverId)}
                                </div>
                              </div>
                            </div>
                            <div className="text-end flex-shrink-0">
                              <span
                                className={`badge ${
                                  stale
                                    ? "bg-secondary-subtle text-secondary"
                                    : "bg-success-subtle text-success"
                                }`}
                              >
                                {stale ? "Stale" : "Live"}
                              </span>
                              <div className="text-secondary text-xs mt-1">
                                {formatAgo(ms, now)}
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default LiveBusMapLayer;
